import type { AttachmentKind } from './types';

export const MAX_ATTACHMENT_BYTES = 80_000_000;
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif']);
const OFFICE_EXTENSIONS = new Set([
  'doc', 'docx', 'docm', 'dot', 'dotx', 'dotm', 'rtf',
  'xls', 'xlsx', 'xlsm', 'xlsb', 'xlt', 'xltx', 'xltm', 'csv', 'tsv',
  'ppt', 'pptx', 'pptm', 'pps', 'ppsx', 'ppsm', 'pot', 'potx', 'potm',
  'odt', 'ods', 'odp', 'wps', 'et', 'dps', 'pages', 'numbers', 'key',
]);

export type PreparedAttachment = {
  blob: Blob;
  originalName: string;
  storedName: string;
  originalType: string;
  kind: AttachmentKind;
  originalSize: number;
};

export function attachmentKind(name: string): AttachmentKind {
  const extension = fileExtension(name);
  if (IMAGE_EXTENSIONS.has(extension)) return 'image';
  if (extension === 'txt') return 'text';
  if (extension === 'md' || extension === 'markdown') return 'markdown';
  if (extension === 'pdf') return 'pdf';
  if (OFFICE_EXTENSIONS.has(extension)) return 'download';
  return 'archive';
}

export async function prepareAttachment(file: File): Promise<PreparedAttachment> {
  if (file.size > MAX_ATTACHMENT_BYTES) throw new Error(`“${file.name}”超过 80 MB，无法上传`);
  const originalName = safeName(file.name);
  const kind = attachmentKind(originalName);
  if (kind !== 'archive') return { blob: file, originalName, storedName: originalName, originalType: file.type || 'application/octet-stream', kind, originalSize: file.size };
  const storedName = `${truncateName(originalName, 245)}.zip`;
  return {
    blob: await singleFileZip(file, originalName),
    originalName,
    storedName,
    originalType: file.type || 'application/octet-stream',
    kind,
    originalSize: file.size,
  };
}

function fileExtension(name: string): string {
  const index = name.lastIndexOf('.');
  return index >= 0 ? name.slice(index + 1).toLowerCase() : '';
}

function safeName(name: string): string {
  const cleaned = name.replace(/[\u0000-\u001f\u007f/\\]/g, '_').trim();
  return truncateName(cleaned || '未命名文件', 255);
}

function truncateName(name: string, max: number): string {
  if (name.length <= max) return name;
  const extensionIndex = name.lastIndexOf('.');
  const extension = extensionIndex > 0 ? name.slice(extensionIndex) : '';
  return `${name.slice(0, Math.max(1, max - extension.length))}${extension}`;
}

async function singleFileZip(file: File, entryName: string): Promise<Blob> {
  const name = new TextEncoder().encode(entryName);
  const crc = await crc32(file);
  const { date, time } = dosTimestamp(file.lastModified || Date.now());
  const local = new Uint8Array(30 + name.length);
  const localView = new DataView(local.buffer);
  localView.setUint32(0, 0x04034b50, true);
  localView.setUint16(4, 20, true);
  localView.setUint16(6, 0x0800, true);
  localView.setUint16(8, 0, true);
  localView.setUint16(10, time, true);
  localView.setUint16(12, date, true);
  localView.setUint32(14, crc, true);
  localView.setUint32(18, file.size, true);
  localView.setUint32(22, file.size, true);
  localView.setUint16(26, name.length, true);
  local.set(name, 30);

  const central = new Uint8Array(46 + name.length);
  const centralView = new DataView(central.buffer);
  centralView.setUint32(0, 0x02014b50, true);
  centralView.setUint16(4, 20, true);
  centralView.setUint16(6, 20, true);
  centralView.setUint16(8, 0x0800, true);
  centralView.setUint16(10, 0, true);
  centralView.setUint16(12, time, true);
  centralView.setUint16(14, date, true);
  centralView.setUint32(16, crc, true);
  centralView.setUint32(20, file.size, true);
  centralView.setUint32(24, file.size, true);
  centralView.setUint16(28, name.length, true);
  centralView.setUint32(42, 0, true);
  central.set(name, 46);

  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(8, 1, true);
  endView.setUint16(10, 1, true);
  endView.setUint32(12, central.length, true);
  endView.setUint32(16, local.length + file.size, true);
  return new Blob([local, file, central, end], { type: 'application/zip' });
}

async function crc32(file: File): Promise<number> {
  const table = crcTable();
  const reader = file.stream().getReader();
  let crc = 0xffffffff;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    for (const byte of value) crc = (crc >>> 8) ^ table[(crc ^ byte) & 0xff];
  }
  return (crc ^ 0xffffffff) >>> 0;
}

let cachedCrcTable: Uint32Array | undefined;
function crcTable(): Uint32Array {
  if (cachedCrcTable) return cachedCrcTable;
  cachedCrcTable = new Uint32Array(256);
  for (let index = 0; index < 256; index++) {
    let value = index;
    for (let bit = 0; bit < 8; bit++) value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    cachedCrcTable[index] = value >>> 0;
  }
  return cachedCrcTable;
}

function dosTimestamp(timestamp: number): { date: number; time: number } {
  const value = new Date(timestamp);
  const year = Math.max(1980, value.getFullYear());
  return {
    date: ((year - 1980) << 9) | ((value.getMonth() + 1) << 5) | value.getDate(),
    time: (value.getHours() << 11) | (value.getMinutes() << 5) | Math.floor(value.getSeconds() / 2),
  };
}
